import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import BdsIconButton from '../../../../../shared/components/v3/Buttons/BdsIconButton/BdsIconButton';
import EmptyCard from '../../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import RankingCard from '../../../../../shared/components/v3/Cards/RankingCard/RankingCard';
import { METRICS_NE_ROUTE } from '../../../../../shared/constants/auth';
import { CATEGORY_ICONS } from '../../../../../shared/constants/benefits';
import { CHEVRON_RIGHT_ICON } from '../../../../../shared/constants/images';
import { BETTERFLIES_RANKING_PAGE_SIZE, METRICS_PERIOD_HISTORICAL } from '../../../../../shared/constants/metrics';
import {
  BENEFITS_HOME_TAB,
  CLICK_ACTION,
  SCREEN_HOME,
  SECTION_ACCESS,
  SECTION_BENEFITS
} from '../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { mapToRankingDataItem } from '../../../../../shared/handlers/dataMapper';
import { IRankingCardData, IRankingCardPage } from '../../../../../shared/interfaces/cards';
import { Company } from '../../../../../shared/services/api/companies/companies';
import { MetricsByCompany } from '../../../../../shared/services/api/metrics/metricsByCompany/metrics';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_HOME } from '../../../../../shared/translations/home';
import { redirectToPage } from '../../../../../shared/utils/utils';
import { isEmptyObject } from '../../../../../shared/utils/validation/validation';

const Ranking = () => {
  const { company } = useContext(CompanyContext);
  const [data, setData] = useState<IRankingCardData>({});
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [useBetterfliesMetrics, setUseBetterfliesMetrics] = useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const getRankingPage = useCallback(
    async (limit: number, offset: number): Promise<IRankingCardPage> => {
      const response = await MetricsByCompany.getBetterfliesRankingMetrics(
        company.id,
        METRICS_PERIOD_HISTORICAL,
        undefined,
        limit,
        offset
      );
      const leaderboard = response?.leaderboard ?? [];
      return {
        items: leaderboard.map(mapToRankingDataItem),
        hasMore: response?.pagination?.hasMore ?? leaderboard.length === limit
      };
    },
    [company.id]
  );

  const fetchData = async () => {
    setUseBetterfliesMetrics(false);
    setLoading(true);
    setError(false);
    try {
      const selectedBenefitsResponse = await Company.getSelectedBenefits(company.id).catch(() => ({}));

      if (isEmptyObject(selectedBenefitsResponse)) {
        setData({});
        setUseBetterfliesMetrics(true);
      } else {
        setData(selectedBenefitsResponse);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const onRedirectPage = () => {
    TagManagerService.pushEvent(CLICK_ACTION, SECTION_ACCESS, {
      ...SECTION_BENEFITS,
      ...SCREEN_HOME,
      quick_access_url: METRICS_NE_ROUTE
    });
    redirectToPage(navigate, METRICS_NE_ROUTE + '?type=benefits');
  };

  useEffect(() => {
    fetchData();
  }, [company.id]);

  return (
    <RankingCard
      key={`${company.id}-${useBetterfliesMetrics}`}
      title={
        useBetterfliesMetrics
          ? t(TRANSLATIONS_HOME.betterfliesRankingCard.title)
          : t(TRANSLATIONS_HOME.rankingCard.title)
      }
      valueText={
        useBetterfliesMetrics
          ? t(TRANSLATIONS_HOME.betterfliesRankingCard.valueText)
          : TRANSLATIONS_HOME.rankingCard.collaborators
      }
      headButton={
        !useBetterfliesMetrics && (
          <BdsIconButton icon={CHEVRON_RIGHT_ICON} action={() => onRedirectPage()} variant="primary" size="sm" />
        )
      }
      tabBarIcons={CATEGORY_ICONS}
      hasTabBar={!useBetterfliesMetrics}
      data={data}
      fetchPage={useBetterfliesMetrics ? getRankingPage : undefined}
      pageSize={BETTERFLIES_RANKING_PAGE_SIZE}
      loading={loading}
      error={error}
      action={fetchData}
      gtmTab={BENEFITS_HOME_TAB}
      emptyRankingCard={
        useBetterfliesMetrics && (
          <EmptyCard
            primaryText={t(TRANSLATIONS_HOME.betterfliesRankingCard.emptyState.emptyStateTitle)}
            secondaryText={t(TRANSLATIONS_HOME.betterfliesRankingCard.emptyState.emptyStateDescription)}
          />
        )
      }
    />
  );
};

export default Ranking;
